"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCheapestRoomTotal = void 0;
const _ = require("lodash");
const orders_1 = require("../orders");
const hotel_1 = require("../hotel");
const dayjs = require("dayjs");
const occupancyDescription_1 = require("../occupancyDescription");
const getCheapestRoomTotal = (item, date, number_of_guests, number_of_nights, margin_discount_percentage) => {
    const availability = pruneAvailability((0, hotel_1.getHotelRoomRatesByDate)(item, date, number_of_nights));
    let currency = null;
    if (availability.length > 0) {
        currency = availability[0].currency;
    }
    const room_rates = [];
    let discount;
    let room_ids = [];
    let room_total = 0;
    let is_sold_out = false;
    if (availability.length > 0) {
        room_ids = availability
            .map((x) => x.availability.map((y) => y.room.id))
            .flat();
        if (availability[0].availability.some((x) => x.board_options.some((y) => y.dates.some((z) => z.is_stop_sell === true)))) {
            is_sold_out = true;
        }
        // Filter out blackout dates for all available rooms
        const room_availability = filterStopSellDates(availability[0]);
        if (room_availability.availability.length > 0) {
            // Calculate total price for each room option
            const roomsWithPrices = room_availability.availability
                .filter((room) => room.board_options.length > 0)
                .map((room) => {
                const singleRates = room.board_options.filter((x) => x.occupancy === 1);
                const doubleRates = room.board_options.filter((x) => x.occupancy === 2);
                // Calculate total price for the room configuration
                const getTotalPrice = (single, double) => {
                    let total = 0;
                    if (singleRates.length > 0 && single > 0) {
                        const singlePrice = (0, orders_1.getRoomRateSummary)(singleRates[0].dates, 1, false, margin_discount_percentage, null).total_sell_price_discounted;
                        total += singlePrice * single;
                    }
                    if (doubleRates.length > 0 && double > 0) {
                        const doublePrice = (0, orders_1.getRoomRateSummary)(doubleRates[0].dates, 2, false, margin_discount_percentage, null).total_sell_price_discounted;
                        total += doublePrice * double;
                    }
                    return total;
                };
                const isEvenGuests = number_of_guests % 2 === 0;
                const numDoubleRooms = Math.floor(number_of_guests / 2);
                const numSingleRooms = isEvenGuests ? 0 : 1;
                return {
                    room,
                    singleRates,
                    doubleRates,
                    totalPrice: getTotalPrice(numSingleRooms, numDoubleRooms),
                    numSingleRooms,
                    numDoubleRooms,
                };
            })
                .filter((option) => option.totalPrice > 0) // Filter out invalid combinations
                .sort((a, b) => a.totalPrice - b.totalPrice); // Sort by total price
            // Get the cheapest valid room option
            const cheapestOption = roomsWithPrices[0];
            if (cheapestOption) {
                // Add the rates for the cheapest room configuration
                for (let i = 0; i < cheapestOption.numDoubleRooms; i++) {
                    room_rates.push({
                        ...cheapestOption.doubleRates[0],
                        room: cheapestOption.room.room,
                    });
                }
                for (let i = 0; i < cheapestOption.numSingleRooms; i++) {
                    room_rates.push({
                        ...cheapestOption.singleRates[0],
                        room: cheapestOption.room.room,
                    });
                }
            }
        }
    }
    let board_type = "";
    const board_types = room_rates.map((x) => x?.type);
    const most_common_board_type = _.maxBy(_.toPairs(_.countBy(board_types)), (x) => x[1]);
    board_type = most_common_board_type ? most_common_board_type[0] : "";
    let promo_rate = false;
    room_rates.forEach((rate) => {
        const rate_discounts = (0, hotel_1.getFilteredRoomDiscounts)(dayjs().format("YYYY-MM-DD"), date, (0, hotel_1.getApplicableDiscountsForRatePolicies)(rate.dates, number_of_nights, dayjs(date).diff(dayjs(), "day")));
        const is_promo_rate = rate?.dates?.some((dateRate) => dateRate.contract_type === "PROMOTIONAL_RATE");
        if (is_promo_rate) {
            promo_rate = true;
        }
        if (rate_discounts) {
            discount = rate_discounts;
        }
        const price_summary = (0, orders_1.getRoomRateSummary)(rate.dates, rate.occupancy, false, margin_discount_percentage, rate_discounts);
        room_total += price_summary.total_sell_price_discounted;
    });
    const book_before_dates = room_rates
        .map((x) => x.dates)
        .flat()
        .map((x) => x.book_before)
        .filter((x) => x !== null);
    return {
        promo_rate: promo_rate,
        discount: discount,
        currency: currency,
        book_before: _.min(book_before_dates) || null,
        total: room_total,
        board_type: board_type,
        is_sold_out: room_total === 0 && is_sold_out ? true : false,
        room_ids: _.uniq(room_ids),
        raw_rates: room_rates,
        summary: transformRoomData(room_rates),
        info: groupRoomsByTypeAndBoard(_.uniqBy(room_rates, "room.id")),
    };
};
exports.getCheapestRoomTotal = getCheapestRoomTotal;
function pruneAvailability(data) {
    if (!Array.isArray(data))
        return [];
    const prunedHotels = data
        .map((hotel) => {
        const prunedAvailability = (hotel.availability || [])
            .map((room) => {
            const prunedBoardOptions = (room.board_options || []).filter((bo) => {
                const hasBlackout = (bo.dates || []).some((d) => d.is_blackout === true);
                return !hasBlackout;
            });
            if (prunedBoardOptions.length === 0)
                return null; // drop room
            return { ...room, board_options: prunedBoardOptions };
        })
            .filter(Boolean);
        if (prunedAvailability.length === 0)
            return null; // drop hotel (no rooms left)
        return { ...hotel, availability: prunedAvailability };
    })
        .filter(Boolean);
    return prunedHotels.length === 0 ? [] : prunedHotels;
}
function filterStopSellDates(data) {
    // Create a deep copy of the data to avoid mutating the original
    const filteredData = JSON.parse(JSON.stringify(data));
    // Filter the availability array
    filteredData.availability = filteredData.availability.filter((availability) => {
        // Filter board options for each availability item
        availability.board_options = availability.board_options.filter((boardOption) => {
            // Check if any date in the board option has is_stop_sell = true
            const hasBlackout = boardOption.dates.some((date) => date.is_stop_sell === true);
            // Keep the board option only if there are no blackout dates
            return !hasBlackout;
        });
        // Keep the availability item only if it still has board options
        return availability.board_options.length > 0;
    });
    return filteredData;
}
function transformRoomData(data) {
    // Count total rooms and get room name
    if (data.length === 0) {
        return {
            rooms: "",
            beds: "",
            board: "",
        };
    }
    // Count total rooms and get room name
    const roomName = data[0].room.name.en;
    const totalRooms = data.length;
    // Count occupancy types
    const occupancyCounts = data.reduce((acc, curr) => {
        const key = curr.occupancy === 1 ? "single" : "double";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});
    // Calculate total guests
    const totalGuests = data.reduce((sum, curr) => sum + curr.occupancy, 0);
    // Get board type
    const boardType = data[0].type === "Room Only" ? "" : "Breakfast included";
    // Format occupancy string
    const occupancyParts = [];
    if (occupancyCounts.double) {
        occupancyParts.push(`${occupancyCounts.double} x Double Occupancy`);
    }
    if (occupancyCounts.single) {
        occupancyParts.push(`${occupancyCounts.single} x Single Occupancy`);
    }
    return {
        rooms: `${totalRooms} x ${roomName}`,
        beds: `${occupancyParts.join(" + ")} - ${totalGuests} guests`,
        board: boardType,
    };
}
const groupRoomsByTypeAndBoard = (rooms) => {
    if (!rooms)
        return [];
    if (rooms.length === 0)
        return [];
    const grouped = rooms.reduce((acc, room) => {
        if (!room.room)
            return acc;
        if (Object.keys(room.room).length === 0)
            return acc;
        const key = `${room.room.name.en}-${room.type}-${room.occupancy}`;
        const images = room.room.images ? JSON.parse(room.room.images) : [];
        if (!acc[key]) {
            acc[key] = {
                id: "",
                images: [],
                name: `${room.room.name.en}`,
                room: room.room,
                board: room.type,
                quantity: 0,
                occupancy: 0,
                occupancy_desc: (0, occupancyDescription_1.occupancyDescription)(room.occupancy),
                extra_beds: 0,
            };
        }
        acc[key].id = room.room.id;
        acc[key].images = images;
        acc[key].quantity += 1;
        acc[key].occupancy += room.occupancy;
        acc[key].extra_beds += room.extra_bed ? 1 : 0;
        return acc;
    }, {});
    return Object.values(grouped);
};
